import React, { useState, useEffect } from "react";

import BreadCrumb from "../../components/Navbars/BreadCrumb";

import MainHeader from "../../components/Headers/MainHeader";
import { Trans, useTranslation } from "react-i18next";
import ProductTags from "../../components/Tags/ProductTags";
import SimpleNotification from "../../components/Notifications/SimpleNotification";
import ModelsAPI from "../../API/models.api";
import ModelVersionsAPI from "../../API/modelVersions.api";
import { validate } from "../../utils/ValidationHelpers";
import modelSchema from "../../schemas/model.schema";
import ValidationError from "../../components/Shared/ValidationError";
import { isEmpty } from "lodash";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";
import { SuccessAlert } from "../../utils/Alerts";
import Select from "react-select";
import VersionTable from "../../components/Tables/VersionTable";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import Pagination from "../../components/Pagination/Pagination";
import {
    getReactSelectStyling,
    getShortSelectMenu,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import LookupsAPI from "../../API/lookups.api";
import { setObject, getObject } from "../../utils/StorageHelpers";
import { useLocation, useHistory } from "react-router-dom";

const CloneModel = () => {
    const location = useLocation();
    const selectedModel = location.state?.selectedModel;
    const history = useHistory();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [organizationDropList, setOrganizationDropList] = useState([]);
    const [selectedModelParams, setSelectedModelParams] = useState({});
    const [usersList, setUsersList] = useState([]);
    const [usersDictionary, setUsersDictionary] = useState({});
    const [selectedParameter, setSelectedParameter] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState([]);
    const [versionsTableData, setVersionsTableData] = useState([]);
    const [isVersionTableLoading, setIsVersionTableLoading] = useState(false);
    const [versionsCurrentPage, setVersionsCurrentPage] = useState(1);
    const [versionsTotalCount, setVersionsTotalCount] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [error, setError] = useState({});
    const [payload, setPayload] = useState({
        modelId: "",
        organization: {},
        user: {},
        modelParameters: {},
    });
    const { t } = useTranslation();

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Select-Model-itemsPerPage", true) || 10
    );

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/models",
            element: () => <Trans i18nKey="models">Models</Trans>,
        },
        {
            element: () => t("cloneModelTitle"),
        },
    ];

    useEffect(() => {
        getOrganizationList();
    }, []);

    useEffect(() => {
        if (!isEmpty(selectedModel)) {
            getParamsFromSelectedModel();
            setPayload({
                ...payload,
                modelId: selectedModel.id,
            });
        }
    }, [selectedModel]);

    useEffect(() => {
        if (payload.organization?.value) getUsers(payload.organization.value);
    }, [payload.organization]);

    useEffect(() => {
        if (selectedParameter?.value) {
            fetchModelParamterVersions(
                selectedParameter.value,
                0,
                itemsPerRequest
            );
        }
    }, [selectedParameter]);

    useEffect(() => {
        validate(
            payload,
            modelSchema.CloneModelSchema,
            setIsFormValid,
            setError
        );
    }, [payload]);

    const _setSelectedVersions = (value) => {
        const elemExist = selectedVersion.filter(
            (elem) => elem.id === value.id
        );
        if (elemExist.length === 0)
            setSelectedVersion([...selectedVersion, value]);
        else {
            setSelectedVersion(
                selectedVersion.filter((elem) => elem.id !== value.id)
            );
        }
    };

    const getOrganizationList = async () => {
        const result = await LookupsAPI.getOrganizations();
        const mappedOrganizations =
            result
                ?.map((elem) => ({
                    label: elem.name,
                    value: elem.id,
                }))
                ?.filter((elem) => elem.label !== selectedModel.organization) ||
            [];
        setOrganizationDropList(mappedOrganizations);
    };

    const getUsers = async (organizationId) => {
        if (usersDictionary[organizationId]) {
            setUsersList(usersDictionary[organizationId]);
            return;
        }
        const result = await LookupsAPI.getSuperiorUsers(organizationId);
        setUsersDictionary({ ...usersDictionary, [organizationId]: result });
        setUsersList(result);
    };

    const fetchModelParamterVersions = async (
        modelParameterId,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsVersionTableLoading(true);
        const result = await ModelVersionsAPI.getModelVersions(
            modelParameterId,
            itemsPerPage,
            {
                excludedIds: payload.modelParameters[modelParameterId]?.map(
                    (elem) => elem.id
                ),
            },
            offset
        );
        setVersionsTotalCount(result?.totalCount);
        setVersionsTableData(result?.data);
        setIsVersionTableLoading(false);

        const parameterVersionsTotalCount =
            (result?.totalCount || 0) +
            (payload.modelParameters[modelParameterId]?.length || 0);

        setSelectedModelParams({
            ...selectedModelParams,
            [selectedParameter.value]: {
                ...selectedModelParams[selectedParameter.value],
                versionsCount: parameterVersionsTotalCount,
            },
        });
    };

    const handleVersionsPageClick = (index) => {
        if (index !== setVersionsCurrentPage && selectedParameter?.value) {
            setVersionsCurrentPage(index);
            fetchModelParamterVersions(selectedParameter.value, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setVersionsCurrentPage(1);
        setObject("Select-Model-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchModelParamterVersions(selectedParameter.value, 0, itemsPerPage);
    };

    const handleSubmitCloneModel = async () => {
        setIsSubmitted(true);
        setIsSubmitting(true);

        let paramsAndVersionsArr = [];

        for (const [key, value] of Object.entries(payload.modelParameters)) {
            paramsAndVersionsArr.push({
                modelParameterId: key,
                versionIds: value.map((elem) => elem.id),
            });
        }

        const submittedCloneBodyMapped = {
            modelId: payload.modelId,
            organizationId: payload.organization.value,
            userId: payload.user.value,
            modelParameters: paramsAndVersionsArr,
        };

        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }
        const result = await ModelsAPI.cloneModel(submittedCloneBodyMapped);

        if (result?.cloned) {
            SuccessAlert(t("cloneModelSuccessMessage"));
            LogCapture.info(
                `Model cloned with id: ${selectedModel.id} to organization: ${payload.organization.value} to the new creator: ${payload.user.value}`,
                ENTITIES.models,
                ACTIONS.models.cloneModel
            );
            history.goBack();
        } else {
            LogCapture.error(
                `Couldn't clone model with id: ${selectedModel.id} to organization: ${payload.organization.value} to the new creator: ${payload.user.value}`
            );
        }
        setIsSubmitting(false);
    };

    const modelVersionHeaders = [
        {
            label: t("active"),
            skipSort: true,
        },
        { label: t("version"), skipSort: true },
        { label: t("description"), skipSort: true },
        { label: t("publishedAt"), skipSort: true },
        { label: t("modelType"), skipSort: true },
        { label: t("range"), skipSort: true },
        {
            label: "R2 Test",
            element: (
                <span className="ml-2">
                    R<sup>2</sup> Test
                </span>
            ),
            skipSort: true,
        },
        { label: t("rmseTest"), skipSort: true },
        {
            label: t("r2Cv"),
            element: (
                <span className="ml-2">
                    R<sup>2</sup> CV
                </span>
            ),
            skipSort: true,
        },
        { label: t("rmseCv"), skipSort: true },
        { label: t("BiasTest"), skipSort: true },
        { label: t("RPDTest"), skipSort: true },
        { label: t("BiasCV"), skipSort: true },
        { label: t("RPDCV"), skipSort: true },
        { label: t("numberOfSamples"), skipSort: true },
        { label: t("supportedAccessories"), skipSort: true },
        { label: t("actions"), skipSort: true, classNames: "sticky-header" },
    ];

    const getParamsFromSelectedModel = async () => {
        const paramsObj = {};
        selectedModel.modelParameters.forEach((modelParameter) => {
            paramsObj[modelParameter.id] = {
                label: modelParameter.parameterName,
                value: modelParameter.id,
            };
        });

        setSelectedModelParams(paramsObj);
    };

    const handleAddParameter = () => {
        if (payload.modelParameters[selectedParameter.value]) {
            setPayload({
                ...payload,
                modelParameters: {
                    ...payload.modelParameters,
                    [selectedParameter.value]: [
                        ...payload.modelParameters[selectedParameter.value],
                        ...selectedVersion,
                    ],
                },
            });
        } else {
            setPayload({
                ...payload,
                modelParameters: {
                    ...payload.modelParameters,
                    [selectedParameter.value]: selectedVersion,
                },
            });
        }

        // reset parameter and versions table
        setSelectedParameter(null);
        setSelectedVersion([]);
        setVersionsTableData([]);
    };

    const handleMakeTags = () => {
        let tagsArr = [];
        if (payload.modelParameters)
            for (const [key, value] of Object.entries(
                payload.modelParameters
            )) {
                value.forEach((element, index) => {
                    const parameter = selectedModelParams[key];
                    parameter &&
                        tagsArr.push({
                            label: `${parameter.label}-${element.version}`,
                            index: `${parameter.value}_${index}`,
                        });
                });
            }
        return tagsArr;
    };

    const removeUnassignedKey = (modelParameters, parameterId) => {
        const parameterKey = selectedModelParams[parameterId]?.value;

        if (isEmpty(modelParameters[parameterKey])) {
            delete modelParameters[parameterKey];
        }
    };

    const handleRemovetag = (tagName, index) => {
        const [parameterId, versionIndex] = index.split("_");
        const clonedObj = { ...payload };

        clonedObj.modelParameters[parameterId].splice(versionIndex, 1);

        removeUnassignedKey(clonedObj.modelParameters, parameterId);

        setPayload(clonedObj);
        if (selectedParameter?.value)
            fetchModelParamterVersions(
                selectedParameter.value,
                0,
                itemsPerRequest
            );
    };

    const getParamsSelect = () =>
        Object.values(selectedModelParams)?.filter((elem) =>
            elem.versionsCount
                ? elem?.versionsCount !==
                  payload.modelParameters[elem.value]?.length
                : true
        );

    const renderButton = (
        id,
        label,
        onClick,
        classNames = "",
        disbaled = false,
        icon = null
    ) => (
        <button
            className={`${classNames} ${disbaled ? "disabled" : ""}`}
            onClick={onClick}
            disabled={disbaled}
            id={id}
        >
            {icon && <i className={`fas fa-${icon}`}></i>}
            {label}
        </button>
    );

    const renderCloneModelContent = () => {
        return (
            <>
                <div className="tags input-group">
                    <h4 className="uppercase text-xs required">
                        <Trans i18nKey="selectOrganization">
                            Select Organization
                        </Trans>
                    </h4>
                    <Select
                        options={organizationDropList}
                        value={
                            isEmpty(payload.organization)
                                ? ""
                                : payload.organization
                        }
                        onChange={(e) => {
                            setPayload({
                                ...payload,
                                organization: e,
                                user: {},
                            });
                        }}
                        placeholder={t("selectPlaceholder", {
                            entity: "Organization",
                        })}
                        theme={getReactSelectTheme}
                        styles={getReactSelectStyling({
                            isMultiSelect: true,
                        })}
                    />
                    {isSubmitted && (
                        <ValidationError errorMessage={error.organization} />
                    )}
                </div>
                <div className="tags input-group">
                    <h4 className="uppercase text-xs required">
                        <Trans i18nKey="selectNewCreator">
                            Select New Creator
                        </Trans>
                    </h4>
                    <Select
                        options={usersList}
                        value={isEmpty(payload.user) ? "" : payload.user}
                        onChange={(e) =>
                            setPayload({
                                ...payload,
                                user: e,
                            })
                        }
                        placeholder={t("selectPlaceholder", {
                            entity: "New Creator",
                        })}
                        theme={getReactSelectTheme}
                        styles={getReactSelectStyling({
                            isMultiSelect: true,
                        })}
                        components={getShortSelectMenu()}
                    />
                    {isSubmitted && (
                        <ValidationError errorMessage={error.user} />
                    )}
                </div>

                <div className="tags input-group">
                    <h4 className="uppercase text-xs required">
                        <Trans i18nKey="selectedParameters">
                            Selected Parameters
                        </Trans>
                    </h4>
                    <ProductTags
                        tags={handleMakeTags()}
                        emptyElement={() => (
                            <div className="w-full">
                                <SimpleNotification
                                    message={t(
                                        "selectedParametersEmptyMessage"
                                    )}
                                />
                            </div>
                        )}
                        removeTagHandler={handleRemovetag}
                    />
                    {isSubmitted && (
                        <ValidationError errorMessage={error.modelParameters} />
                    )}
                </div>
                <div className="flex justify-between gap-2">
                    <div className="tags input-group w-full">
                        <h4 className="uppercase text-xs required">
                            <Trans i18nKey="parameters">Parameters</Trans>
                        </h4>
                        <Select
                            options={getParamsSelect()}
                            value={selectedParameter || ""}
                            onChange={setSelectedParameter}
                            placeholder={t("selectPlaceholder", {
                                entity: "Parameter",
                            })}
                            theme={getReactSelectTheme}
                            styles={getReactSelectStyling({
                                isMultiSelect: false,
                            })}
                        />
                    </div>
                    {renderButton(
                        "add-parameter-btn",
                        t("add"),
                        handleAddParameter,
                        "default-orange-outline-btn self-center w-16 h-8.5 mt-2",
                        isEmpty(selectedParameter) || isEmpty(selectedVersion)
                    )}
                </div>

                <div className="tags input-group">
                    <h4 className="uppercase text-xs required">
                        <Trans i18nKey="versions">Versions</Trans>
                    </h4>
                    <LoadingOverlay
                        active={isVersionTableLoading}
                        text="Loading..."
                    >
                        <VersionTable
                            headers={modelVersionHeaders}
                            rows={versionsTableData}
                            showSelected={true}
                            selectedVersion={selectedVersion}
                            setSelectedVersion={_setSelectedVersions}
                            emptyMessage={t("versionTableEmptyMessage")}
                            multiSelectVersion
                        />
                        <Pagination
                            currentPage={versionsCurrentPage}
                            setCurrentPage={handleVersionsPageClick}
                            totalCount={versionsTotalCount}
                            sizePerPage={itemsPerRequest}
                            handleItemsPerPage={handleItemsPerPage}
                        />
                    </LoadingOverlay>
                </div>
            </>
        );
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <MainHeader header={t("cloneModelTitle")} />
            <div className="card-table shadow">
                {renderCloneModelContent()}
                <div className="flex justify-between">
                    {renderButton(
                        "navigation-back-btn",
                        t("goBack"),
                        history.goBack,
                        "back-btn",
                        false,
                        "caret-left mr-1"
                    )}
                    {renderButton(
                        "clone-btn",
                        t("clone"),
                        handleSubmitCloneModel,
                        "default-orange-outline-btn",
                        isSubmitting
                    )}
                </div>
            </div>
        </>
    );
};

export default CloneModel;
